import React from 'react';
import {
  BodyTextRegularTertiary,
  HeadingRegularSenary,
} from '@/shared/ui/typography';
import { DetailItem, DetailItems } from '../styles';
import { Box } from '@/shared/ui/box';
import { formatCurrency } from '@/shared/libs/format-currency';

const hourSuffix = {
  en: 'hr',
  ru: 'ч',
};

export const BonusDetailsContent = ({ bonus, user, t, tWallet, locale }) => {
  if (!bonus) return null;

  const {
    percent,
    wager,
    minDeposit,
    maxDeposit,
    maxBet,
    liveHours,
    maxWinMult,
    maxWin,
    spins,
    bet,
    bonusSum,
    type,
  } = bonus;

  const currencySettings = {
    currency: user.currency,
    currencySymbol: user.currencySymbol,
  };

  const hoursSuffixText = hourSuffix[locale] || hourSuffix.en;

  // Общий компонент для отображения максимального выигрыша
  const renderMaxWin = () => (
    <DetailItem>
      <BodyTextRegularTertiary color="#AEB8D1">
        {t('card.max_win')}
      </BodyTextRegularTertiary>
      <HeadingRegularSenary>
        {maxWinMult
          ? `x${Number(maxWinMult).toFixed(Number(maxWinMult) % 1 === 0 ? 0 : 2)}`
          : formatCurrency(maxWin, currencySettings)}
      </HeadingRegularSenary>
    </DetailItem>
  );

  // Общий компонент для отображения времени жизни
  const renderLiveHours = () => (
    <DetailItem>
      <BodyTextRegularTertiary color="#AEB8D1">
        {tWallet('fields.expire')}
      </BodyTextRegularTertiary>
      <HeadingRegularSenary>
        {`${liveHours} ${hoursSuffixText}`}
      </HeadingRegularSenary>
    </DetailItem>
  );

  const renderFreespinDetails = () => {
    const hasDetails =
      spins || bet || minDeposit || liveHours || maxWinMult || maxWin;
    if (!hasDetails) return null;

    return (
      <Box $display="flex" $flexDirection="column" $gap="5px">
        {(spins || bet) && (
          <DetailItems>
            {spins && (
              <DetailItem>
                <BodyTextRegularTertiary color="#AEB8D1">
                  {t('card.spins')}
                </BodyTextRegularTertiary>
                <HeadingRegularSenary>{spins}</HeadingRegularSenary>
              </DetailItem>
            )}
            {bet && (
              <DetailItem>
                <BodyTextRegularTertiary color="#AEB8D1">
                  {t('card.bet')}
                </BodyTextRegularTertiary>
                <HeadingRegularSenary>
                  {formatCurrency(bet, currencySettings)}
                </HeadingRegularSenary>
              </DetailItem>
            )}
          </DetailItems>
        )}

        {minDeposit && (
          <DetailItems>
            <DetailItem>
              <BodyTextRegularTertiary color="#AEB8D1">
                {t('card.min_deposit')}
              </BodyTextRegularTertiary>
              <HeadingRegularSenary>
                {formatCurrency(minDeposit, currencySettings)}
              </HeadingRegularSenary>
            </DetailItem>
          </DetailItems>
        )}

        {(liveHours || maxWinMult || maxWin) && (
          <DetailItems>
            {liveHours && renderLiveHours()}
            {(maxWinMult || maxWin) && renderMaxWin()}
          </DetailItems>
        )}
      </Box>
    );
  };

  const renderDepositDetails = () => {
    const hasDetails =
      percent ||
      wager ||
      minDeposit ||
      maxDeposit ||
      maxBet ||
      liveHours ||
      maxWinMult ||
      maxWin;
    if (!hasDetails) return null;

    return (
      <Box $display="flex" $flexDirection="column" $gap="5px">
        {(percent || wager) && (
          <DetailItems>
            {percent && (
              <DetailItem>
                <BodyTextRegularTertiary color="#AEB8D1">
                  {t('card.percent')}
                </BodyTextRegularTertiary>
                <HeadingRegularSenary>{percent}%</HeadingRegularSenary>
              </DetailItem>
            )}
            {wager && (
              <DetailItem>
                <BodyTextRegularTertiary color="#AEB8D1">
                  {t('card.wager')}
                </BodyTextRegularTertiary>
                <HeadingRegularSenary>X{wager}</HeadingRegularSenary>
              </DetailItem>
            )}
          </DetailItems>
        )}

        {(minDeposit || maxDeposit) && (
          <DetailItems>
            {minDeposit && (
              <DetailItem>
                <BodyTextRegularTertiary color="#AEB8D1">
                  {t('card.min_deposit')}
                </BodyTextRegularTertiary>
                <HeadingRegularSenary>
                  {formatCurrency(minDeposit, currencySettings)}
                </HeadingRegularSenary>
              </DetailItem>
            )}
            {maxDeposit && (
              <DetailItem>
                <BodyTextRegularTertiary color="#AEB8D1">
                  {t('card.max_deposit')}
                </BodyTextRegularTertiary>
                <HeadingRegularSenary>
                  {formatCurrency(maxDeposit, currencySettings)}
                </HeadingRegularSenary>
              </DetailItem>
            )}
          </DetailItems>
        )}

        {maxBet && (
          <DetailItems>
            <DetailItem>
              <BodyTextRegularTertiary color="#AEB8D1">
                {t('card.max_bet')}
              </BodyTextRegularTertiary>
              <HeadingRegularSenary>
                {formatCurrency(maxBet, currencySettings)}
              </HeadingRegularSenary>
            </DetailItem>
          </DetailItems>
        )}

        {(liveHours || maxWinMult || maxWin) && (
          <DetailItems>
            {liveHours && renderLiveHours()}
            {(maxWinMult || maxWin) && renderMaxWin()}
          </DetailItems>
        )}
      </Box>
    );
  };

  const renderNonDepositDetails = () => (
    <Box $display="flex" $flexDirection="column" $gap="5px">
      {(bonusSum || wager) && (
        <DetailItems>
          {bonusSum && (
            <DetailItem>
              <BodyTextRegularTertiary color="#AEB8D1">
                {t('card.amount')}
              </BodyTextRegularTertiary>
              <HeadingRegularSenary>
                {formatCurrency(bonusSum, currencySettings)}
              </HeadingRegularSenary>
            </DetailItem>
          )}
          {wager && (
            <DetailItem>
              <BodyTextRegularTertiary color="#AEB8D1">
                {t('card.wager')}
              </BodyTextRegularTertiary>
              <HeadingRegularSenary>X{wager}</HeadingRegularSenary>
            </DetailItem>
          )}
        </DetailItems>
      )}
      {(bonusSum || wager) && (
        <DetailItems>
          <DetailItem $fill={true}>
            <BodyTextRegularTertiary color="#AEB8D1">
              {t('card.wager_result')}
            </BodyTextRegularTertiary>
            <HeadingRegularSenary>
              {formatCurrency(bonusSum * wager, currencySettings)}
            </HeadingRegularSenary>
          </DetailItem>
        </DetailItems>
      )}

      {(liveHours || maxWinMult || maxWin) && (
        <DetailItems>
          {liveHours && renderLiveHours()}
          {(maxWinMult || maxWin) && renderMaxWin()}
        </DetailItems>
      )}
    </Box>
  );

  const bonusTypeRenderers = {
    freespin: renderFreespinDetails,
    dep: renderDepositDetails,
    nondep: renderNonDepositDetails,
  };

  return bonusTypeRenderers[type]?.();
};
